import { autoUpdate, offset, useFloating } from "@floating-ui/react";
import { camelizeKeys } from "humps";
import { useCanEditAuditRoles } from "PFApp/activities/hooks/use_can_edit_audit_roles";
import { useManageTagsPermissions } from "PFApp/activities/show/activity_page/parts/tag/use_manage_tags_permissions";
import { KPIsReviewSidepanelData } from "PFApp/components/kpis_review_sidepanel/kpis_review_sidepanel.types";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import useIsRoleRemovalPermitted from "PFApp/use_is_role_removal_permitted";
import { Inline } from "PFComponents/containers";
import { DropdownOption } from "PFComponents/dropdown";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { isOpen } from "PFCore/helpers/activities";
import { isSubtemplate } from "PFCore/helpers/templates";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { Activity, Engagement, FeatureFlag, Role } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./activity_actions.module.scss";
import { usePriorityVisibility } from "./hooks/use_priority_visibility";
import PriorityDropdown from "./priority_dropdown";

type UseOptions = {
  item: Activity | Role | Engagement;
  pinnedIds: number[];
  type: TemplateKey;
  coowned: boolean;
  onEdit: VoidFunction;
  onClone: VoidFunction;
  onReopen: VoidFunction;
  onShare?: VoidFunction;
  onPrioritySelected: VoidFunction;
  onRequestHelp?: (id: number) => void;
  onRemove: VoidFunction;
  onClose: VoidFunction;
  onPinned?: (isPinned: boolean) => void;
  onManageTags?: VoidFunction;
  onKPIsReviewClick?: (data: KPIsReviewSidepanelData) => void;
};

type UseOptionsReturn = {
  options: DropdownOption[];
  onClose: VoidFunction;
};

export const useThreeDotsMenuOptions = ({
  item,
  pinnedIds,
  type,
  coowned,
  onEdit,
  onClone,
  onReopen,
  onShare,
  onRequestHelp,
  onPrioritySelected,
  onRemove,
  onClose,
  onPinned,
  onManageTags,
  onKPIsReviewClick
}: UseOptions): UseOptionsReturn => {
  const { t } = useTranslation("workflow", { keyPrefix: "parts.threeDotsMenu" });
  const { t: translation } = useTranslation("translation");
  const removePermitted = useIsRoleRemovalPermitted();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isEngagementKPIFeatureEnabled = isFeatureEnabled(FeatureFlag.EngagementKPIs);

  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: "left-start",
    strategy: "fixed",
    middleware: [offset(20)],
    whileElementsMounted: autoUpdate
  });

  const isAudit = type.includes("audit");
  const isRole = type.includes("role");
  const template = useTemplate({ key: type });
  const isSubtemplateMissing =
    // TODO: [SP-6880] Fix `any` type. Add `subtemplate_key` to Engagement type
    isRole &&
    !!(item as any).subtemplate_key &&
    !!template &&
    !isSubtemplate(template, (item as any).subtemplate_key);
  const isPinned = pinnedIds.includes(item.id);

  const { isPriorityAvailable, isPriorityDisabled, isPriorityRequired } = usePriorityVisibility(
    item,
    template
  );

  const { canEditAuditRole } = useCanEditAuditRoles({
    coowned: item.coowned,
    templateId: (item as Role).template_id,
    workflowState: (item as Role).workflow_state
  });

  const { canReadWriteTags } = useManageTagsPermissions({
    isCoowned: item.coowned,
    profile: item.profile,
    templateId: template?.id
  });

  const KPIs = camelizeKeys((item as Engagement).kpis) as KPIsReviewSidepanelData["KPIs"];

  const items: (DropdownOption & { visible: boolean })[] = [
    {
      id: "pinned",
      visible: !!onPinned,
      displayElement: isPinned ? t("unmarkImportant") : t("markAsImportant"),
      item: () => onPinned?.(!isPinned)
    },
    {
      id: "review_KPIs",
      visible: !!onKPIsReviewClick && isEngagementKPIFeatureEnabled && KPIs?.length > 0,
      displayElement: t("reviewKPIs"),
      item: () =>
        onKPIsReviewClick?.({
          engagementId: item.id,
          engagementName: item.name,
          KPIs
        })
    },
    {
      id: "edit",
      visible: coowned && !isSubtemplateMissing && (!isAudit || canEditAuditRole),
      displayElement: translation("edit"),
      item: onEdit
    },
    {
      id: "reopen",
      visible: isAudit && isRole && coowned && !isOpen(item),
      displayElement: translation("reopen"),
      item: onReopen
    },
    {
      id: "share",
      visible: !!onShare,
      displayElement: t("share"),
      item: onShare
    },
    {
      id: "clone",
      visible: !!template && !template.hidden && template.create_permitted && !isSubtemplateMissing,
      displayElement: t("clone"),
      item: onClone
    },
    {
      id: "request_help",
      visible: coowned,
      displayElement: t("requestHelp"),
      item: onRequestHelp
    },
    {
      id: "manage_tags",
      visible: canReadWriteTags && !!onManageTags,
      displayElement: t("manageTags"),
      item: onManageTags
    },
    {
      id: "priority",
      visible: coowned && isPriorityAvailable,
      displayElement: (
        <div>
          <Inline ref={refs.setReference} alignItems="center" gap="spacingSm" className={css.priorityButton}>
            <Typography variant="bodyRegular" clipOverflow>
              {t("priority.option")}
            </Typography>
            <Icon name="caret-right" size="xxs" />
          </Inline>
          {showPriorityDropdown && (
            <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 20 }}>
              <PriorityDropdown
                activity={item}
                disabled={isPriorityDisabled}
                required={isPriorityRequired}
                onSelected={onPrioritySelected}
              />
            </div>
          )}
        </div>
      ),
      item: {
        keepOpen: true,
        func: () => setShowPriorityDropdown((prev) => !prev)
      }
    },
    {
      id: "remove",
      visible: !isAudit && coowned && removePermitted,
      displayElement: (
        <Typography variant="bodyRegular" className={css.dangerButton} clipOverflow>
          {translation("remove")}
        </Typography>
      ),
      item: onRemove
    },
    {
      id: "close",
      visible: coowned && isOpen(item),
      displayElement: (
        <Typography variant="bodyRegular" className={css.dangerButton} clipOverflow>
          {translation("close")}
        </Typography>
      ),
      item: onClose
    }
  ];

  return {
    options: items.filter(({ visible }) => visible),
    onClose: () => setShowPriorityDropdown(false)
  };
};
